// =============================================================================
// components/ui/MediaTopBar.tsx — floating translucent top bar over FULL-BLEED
// media (DARK). Back chevron · title · a LIVE badge pill with a viewer count.
//
// Reference: the IG live-player top chrome — a translucent bar floating at the
// top over the video (content runs underneath), not an opaque strip. Absolute
// at insets.top + spacing.sm with gutter side margins (apple-design SKILL §12:
// translucent chrome, content scrolls under; §7 anchored at the top edge).
//
// Built from existing primitives: GlassSurface (mode='dark', variant='bar') for
// the floating body, GlassPill for the LIVE badge, Icon for the back chevron.
// =============================================================================
import React from 'react';
import { View, Text, Pressable, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from './Icon';
import { GlassSurface } from '../glass';
import { glassTokens } from '../glass/tokens';
import { colors, spacing, fonts, layout } from '../../constants/theme';

export interface MediaTopBarProps {
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  /** Show the LIVE badge with a viewer count. */
  live?: boolean;
  viewerCount?: number;
  /** Optional trailing control (e.g. a close/overflow). */
  trailing?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 >= 100_000 ? 1 : 0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 >= 100 ? 1 : 0)}K`;
  return String(n);
}

export default function MediaTopBar({
  title, subtitle, onBack, live = false, viewerCount, trailing, style,
}: MediaTopBarProps) {
  const insets = useSafeAreaInsets();

  return (
    <View
      pointerEvents="box-none"
      style={[styles.wrap, { top: insets.top + spacing.sm }, style]}
    >
      <GlassSurface
        mode="light"
        variant="bar"
        radius={glassTokens.radius.pill}
        contentStyle={styles.bar}
      >
        {onBack ? (
          <Pressable
            onPress={onBack}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={styles.iconBtn}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="chevron-back" size={22} color={colors.white} />
          </Pressable>
        ) : <View style={styles.iconBtn} />}

        <View style={styles.center}>
          {title ? <Text style={styles.title} numberOfLines={1}>{title}</Text> : null}
          {subtitle ? <Text style={styles.subtitle} numberOfLines={1}>{subtitle}</Text> : null}
        </View>

        <View style={styles.trailing}>
          {live ? (
            <View style={styles.liveBadge}>
              <View style={styles.liveDot} />
              <Text style={styles.liveText}>LIVE</Text>
              {typeof viewerCount === 'number' ? (
                <>
                  <View style={styles.liveSep} />
                  <Ionicons name="eye" size={12} color={colors.white} />
                  <Text style={styles.viewerText}>{formatCount(viewerCount)}</Text>
                </>
              ) : null}
            </View>
          ) : null}
          {trailing}
        </View>
      </GlassSurface>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: layout.gutter,
    right: layout.gutter,
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    minHeight: 44,
  },
  iconBtn: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center' },
  center: { flex: 1, paddingHorizontal: spacing.xs },
  title: { ...fonts.bold, fontSize: 15, color: colors.white },
  subtitle: { ...fonts.medium, fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 1 },
  trailing: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingRight: spacing.xs },
  liveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    borderRadius: glassTokens.radius.pill,
    backgroundColor: colors.error,
  },
  liveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.white },
  liveText: { ...fonts.extrabold, fontSize: 11, letterSpacing: 0.6, color: colors.white },
  liveSep: { width: StyleSheet.hairlineWidth, height: 12, backgroundColor: 'rgba(255,255,255,0.4)', marginHorizontal: 2 },
  viewerText: { ...fonts.bold, fontSize: 11, color: colors.white },
});
